import React from 'react';
import { 
  X, 
  Briefcase, 
  DollarSign, 
  TrendingUp,
  MapPin,
  Building2,
  PieChart
} from 'lucide-react';
import './styles.scss';

const RegionalMarketDetailModal = ({ isOpen, onClose, occupation }) => {
  if (!isOpen || !occupation) return null;

  // Polymorphic mapping for MSA records
  const median = occupation.annualMedianWage || occupation.a_median || 0;
  const mean = occupation.annualMeanWage || occupation.a_mean || 0;
  const employment = occupation.totalEmployment || occupation.tot_emp || 0;
  const areaTitle = occupation.areaTitle || occupation.area_title || 'Metropolitan Area';
  const occTitle = occupation.occTitle || occupation.occ_title || 'Unknown Role';
  const occCode = occupation.occCode || occupation.occ_code || '00-0000';

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(val || 0);
  };

  const formatNumber = (val) => {
    return new Intl.NumberFormat('en-US').format(val || 0);
  };

  return (
    <div className="modal-overlay-glass" onClick={onClose}>
      <div 
        className="regional-detail-modal-premium animate-scale-in" 
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-header">
          <div className="header-content">
            <div className="icon-badge">
              <Briefcase size={24} />
            </div>
            <div className="title-stack">
              <span className="occ-code-pill">{occCode}</span>
              <h2>{occTitle}</h2>
            </div>
          </div>
          <button className="close-btn-round" onClick={onClose} aria-label="Close modal">
            <span style={{ fontSize: '32px', lineHeight: 0.8, fontWeight: '300', color: '#64748b' }}>&times;</span>
          </button>
        </header>

        <div className="modal-body-scrollable">
          <section className="metadata-grid">
            <div className="meta-card">
              <MapPin size={18} className="meta-icon" />
              <div className="meta-info">
                <label>Regional Context</label>
                <p>{areaTitle}</p>
              </div>
            </div>
            <div className="meta-card">
              <Building2 size={18} className="meta-icon" />
              <div className="meta-info">
                <label>Industry Scope</label>
                <p>{occupation.naicsTitle || occupation.naics_title || 'Regional Multisector'}</p>
              </div>
            </div>
          </section>

          <section className="market-stats-deep-dive">
             <div className="stat-highlight-card">
                <div className="stat-main">
                  <div className="stat-item">
                    <label>Annual Median Wage</label>
                    <div className="value-row">
                      <DollarSign size={24} className="text-emerald" />
                      <span className="value">{formatCurrency(median)}</span>
                    </div>
                    <p className="hint">The wage at which half of the workers in the region earn more and half earn less.</p>
                  </div>
                  <div className="divider" />
                  <div className="stat-item">
                    <label>Annual Mean Wage</label>
                    <div className="value-row">
                      <TrendingUp size={24} className="text-indigo" />
                      <span className="value">{formatCurrency(mean)}</span>
                    </div>
                    <p className="hint">The average wage calculated by dividing the total regional wages by the number of employees.</p>
                  </div>
                </div>
             </div>

             <div className="secondary-stats-grid">
                <div className="mini-card">
                  <div className="card-top">
                    <TrendingUp size={16} />
                    <span>Total Employment</span>
                  </div>
                  <div className="card-value">{formatNumber(employment)}</div>
                  <p>Active positions in the local market</p>
                </div>
                <div className="mini-card">
                  <div className="card-top">
                    <PieChart size={16} />
                    <span>Location Quotient</span>
                  </div>
                  <div className="card-value">{occupation.locationQuotient || occupation.loc_quotient || '1.0'}</div>
                  <p>Concentration relative to national average</p>
                </div>
             </div>
          </section>
        </div>

        <footer className="modal-footer">
          <p className="data-source-tag">Source: BLS Metropolitan Area Occupational Employment Statistics (OEWS)</p>
          <button className="btn-primary" onClick={onClose}>
            Close Explorer
          </button>
        </footer>
      </div>
    </div>
  );
};

export default RegionalMarketDetailModal;
